<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :style="{ width: '28rem' }"
  >
    <template #header>
      <div>
        <h2 class="text-navy-800 text-base font-semibold">Invite Member</h2>
        <p class="text-[13px] text-neutral-500">
          Send an invitation to join this company
        </p>
      </div>
    </template>

    <div class="space-y-4">
      <div>
        <label class="text-navy-800 mb-1 block text-[13px] font-medium">
          Email Address
        </label>
        <input
          v-model="email"
          type="email"
          placeholder="colleague@example.com"
          class="text-navy-800 placeholder-navy-300 w-full rounded-md border border-neutral-300 px-3 py-[9px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
        />
      </div>
      <div>
        <AppSelect
          v-model="role"
          label="Role"
          placeholder="Select a role"
          :options="roleOptions"
        />
        <p class="mt-1 text-xs text-neutral-400">
          {{ roleDescription }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <AppButton variant="secondary" size="sm" @click="visible = false">
          Cancel
        </AppButton>
        <AppButton
          :loading="sending"
          :disabled="!canSend"
          @click="sendInvitation"
        >
          Send Invitation
        </AppButton>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { useToast } from "primevue/usetoast"
import { computed, ref, watch } from "vue"

import { apolloClient } from "../../queries/client"
import CREATE_INVITATION from "../../queries/invitation/create.gql"
import AppButton from "../ui/AppButton.vue"
import AppSelect from "../ui/AppSelect.vue"

interface Props {
  companyId: string
}

const props = defineProps<Props>()

const visible = defineModel<boolean>("visible", { default: false })

const emit = defineEmits<{
  invited: []
}>()

const toast = useToast()
const email = ref("")
const role = ref("")
const sending = ref(false)

const roleOptions = [
  { label: "Admin", value: "ADMIN" },
  { label: "Contributor", value: "CONTRIBUTOR" },
  { label: "Viewer", value: "VIEWER" },
]

const roleDescription = computed(() => {
  if (role.value === "ADMIN")
    return "Full access — manage members, agents, and company settings."
  if (role.value === "CONTRIBUTOR")
    return "Can create and manage orders, but cannot manage company settings."
  if (role.value === "VIEWER")
    return "Read-only access to orders and company information."
  return "Choose a role for the new member."
})

const canSend = computed(
  () => email.value.trim().length > 0 && role.value.length > 0,
)

watch(visible, (isOpen) => {
  if (isOpen) {
    email.value = ""
    role.value = ""
  }
})

const sendInvitation = async () => {
  if (!canSend.value) return

  sending.value = true
  try {
    const result = await apolloClient.mutate({
      mutation: CREATE_INVITATION,
      variables: {
        companyId: props.companyId,
        email: email.value.trim(),
        role: role.value,
      },
    })
    const data = result.data?.createInvitation
    if (data?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: data.errors.join(", "),
        life: 4000,
      })
      return
    }

    toast.add({
      severity: "success",
      summary: "Invitation Sent",
      detail: `Invitation sent to ${email.value.trim()}`,
      life: 3000,
    })

    emit("invited")
    visible.value = false
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to send invitation",
      life: 4000,
    })
  } finally {
    sending.value = false
  }
}
</script>
